<template>
  <div class="scouting-admin-page" v-if="canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="title"
      :grid-ref="$refs[gridRefKey]"
      :show-add="false"
      :show-filter="true"
      :show-columns="true"
      :show-export="true"
      :show-refresh="true"
      @export="exportGrid"
      @columns="openColumnChooser"
    />
    <GridFilter :grid-ref="$refs[gridRefKey]" :visible.sync="filterPanelVisible" :title="$t('common.filter')">
      <DateRangeFilter :dateFrom.sync="filters.creation_date_from" :dateTo.sync="filters.creation_date_to" />
    </GridFilter>
    <DxDataGrid
      key-expr="uuid"
      :ref="gridRefKey"
      :data-source="ScoutingsDataSource"
      :remote-operations="gridRemoteOperations"
      :column-min-width="120"
      style="min-height: 350px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :element-attr="gridAttributes"
    >
      <DxColumn
        data-field="reported_date"
        data-type="date"
        :format="getDxGridDefaultDateFormat || 'dd-MM-yyyy'"
        :caption="$t('common.date')"
        :allow-filtering="true"
        :filter-operations="['between']"
        selected-filter-operation="between"
        :allow-sorting="true"
      ></DxColumn>
      <DxColumn
        data-field="engineer_name"
        :caption="$t('scouting.engineer_name')"
        :allow-header-filtering="false"
        :filter-operations="['contains']"
        :allow-sorting="true"
      ></DxColumn>
      <DxColumn
        data-field="parcel_name"
        :caption="$t('common.parcel_name')"
        :allow-header-filtering="false"
        :filter-operations="['contains']"
        :allow-sorting="true"
      ></DxColumn>
      <DxColumn
        data-field="bbch"
        :caption="$t('scouting.bbch')"
        :allow-header-filtering="false"
        :filter-operations="['contains']"
        :allow-sorting="false"
        :calculate-cell-value="getBbchExportValue"
        cell-template="bbchTemplate"
      ></DxColumn>
      <DxColumn
        data-field="diseases"
        :caption="$t('scouting.diseases')"
        :allow-header-filtering="false"
        :filter-operations="['contains']"
        :allow-sorting="false"
        :calculate-cell-value="getDiseasesExportValue"
        cell-template="diseasesTemplate"
      ></DxColumn>
      <DxColumn
        data-field="infection_level"
        :caption="$t('scouting.infection_level')"
        data-type="number"
        :allow-header-filtering="false"
        :allow-filtering="true"
        :filter-operations="['=']"
        :allow-sorting="false"
        cell-template="infectionLevelTemplate"
        :calculate-cell-value="getInfectionLevelValue"
      >
        <DxLookup :data-source="infectionLevelList" value-expr="id" display-expr="label" />
      </DxColumn>
      <DxColumn
        data-field="notes"
        :caption="$t('common.notes')"
        :allow-header-filtering="false"
        :filter-operations="['contains']"
        :allow-sorting="false"
        :word-wrap-enabled="true"
        cell-template="notesTemplate"
      ></DxColumn>
      <DxColumn
        data-field="note_1"
        :caption="$t('scouting.bbch_comment')"
        :allow-header-filtering="false"
        :filter-operations="['contains']"
        :allow-sorting="false"
        :word-wrap-enabled="true"
      ></DxColumn>
      <DxColumn
        data-field="note_2"
        :caption="$t('scouting.disease_comment')"
        :allow-header-filtering="false"
        :filter-operations="['contains']"
        :allow-sorting="false"
        :word-wrap-enabled="true"
      ></DxColumn>
      <DxColumn
        data-field="images"
        :caption="$t('scouting.images')"
        :allow-filtering="false"
        :allow-sorting="false"
        :allow-exporting="false"
        cell-template="imagesTemplate"
      ></DxColumn>
      <DxColumn
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        :width="60"
        cell-template="dynamicMenuCell"
      ></DxColumn>

      <template #bbchTemplate="{ data }">
        <span>{{ formatBbch(data.data) }}</span>
      </template>

      <template #diseasesTemplate="{ data }">
        <div>
          <div v-for="(disease, idx) in data.data.diseases" :key="disease.uuid || idx">
            {{ disease.name }}
          </div>
        </div>
      </template>

      <template #infectionLevelTemplate="{ data }">
        <div>
          <div v-for="(disease, idx) in data.data.diseases" :key="disease.uuid || idx">
            {{ infectionLevelLabel(disease.infection_level) }}
          </div>
        </div>
      </template>

      <template #notesTemplate="{ data }">
        <WrapNotes :data="data.data" />
      </template>

      <template #imagesTemplate="{ data }">
        <ScoutingImages :data="data.data" />
      </template>

      <template #dynamicMenuCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :can-edit="false"
          :can-delete="canDelete"
          :allow-deleting="allowDeleting"
        />
      </template>

      <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" :use-icons="true" mode="row">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('scouting.title'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>

      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="false" />
      <DxScrolling mode="standard" show-scrollbar="always" :use-native="false" column-rendering-mode="virtual" />
      <DxFilterRow :visible="true" :show-operation-chooser="true" apply-filter="auto" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'Scoutings'" />
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxHeaderFilter,
  DxFilterRow,
  DxEditing,
  DxTexts,
  DxColumnChooser,
  DxColumnFixing,
  DxScrolling,
  DxExport,
  DxLookup,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import Forbidden from "@/pages/errors/Forbidden.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import GridFilter from "@/components/DxGrid/GridFilter.vue";
import DateRangeFilter from "@/components/DxGrid/DateRangeFilter.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import WrapNotes from "~/pages/scouting/admin/partials/WrapNotes.vue";
import ScoutingImages from "@/pages/scouting/admin/partials/ScoutingImages.vue";
import { canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";

export default {
  name: "ScoutingsList",
  middleware: ["auth", "check-company-admin"],
  components: {
    GridPanelHeader,
    GridFilter,
    DateRangeFilter,
    DxActionMenu,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxHeaderFilter,
    DxFilterRow,
    DxEditing,
    DxTexts,
    DxColumnChooser,
    DxColumnFixing,
    DxScrolling,
    DxExport,
    DxLookup,
    WrapNotes,
    ScoutingImages,
    Forbidden,
  },
  data() {
    return {
      filterPanelVisible: false,
      filters: {
        creation_date_from: null,
        creation_date_to: null,
      },
      gridAttributes: {
        class: "smagria-grid scouting-list-grid",
      },
      gridRefKey: "ScoutingsGrid",
      exportLimitOverride: null,
      exportSortOverride: null,
      exportFilterOverride: null,
      infectionLevelList: [
        { id: 1, label: "Low" },
        { id: 2, label: "Medium" },
        { id: 3, label: "High" },
      ],
      ScoutingsDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          const pageLimit = loadOptions.take || this.exportLimitOverride || 15;
          const pageNumber = loadOptions.skip != null && loadOptions.take ? (loadOptions.skip + loadOptions.take) / pageLimit : 1;
          const sortDescriptors =
            Array.isArray(loadOptions.sort) && loadOptions.sort.length
              ? loadOptions.sort
              : Array.isArray(this.exportSortOverride)
              ? this.exportSortOverride
              : [];
          const sort = sortDescriptors.length ? sortDescriptors[0] : null;

          const activeFilter = loadOptions.filter || this.exportFilterOverride;

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            ...this.parseGridFilter(activeFilter),
          };

          if (this.filters.creation_date_from) {
            payload.creation_date_from = format(new Date(this.filters.creation_date_from), "yyyy-MM-dd");
          }
          if (this.filters.creation_date_to) {
            payload.creation_date_to = format(new Date(this.filters.creation_date_to), "yyyy-MM-dd");
          }

          if (sort) {
            payload.sortBy = sort.selector;
            payload.sortDirection = sort.desc ? "DESC" : "ASC";
          }

          try {
            const response = await this.getFilteredScoutings(payload);
            dataSource.data = Array.isArray(response) ? response : [];
            dataSource.totalCount = this.$store.getters["common/pagination"]?.total ?? dataSource.data.length;

            return dataSource;
          } catch (error) {
            console.error("Error fetching scoutings:", error);
            throw error;
          }
        },
        remove: async (key) => {
          await this.deleteObject({ uuid: key });
        },
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 25, 100],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SCOUTINGS,
        permission_type: permissionTypes.module,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    title() {
      return this.$t("scouting.title");
    },
    dataGridInstance() {
      return this.$refs[this.gridRefKey]?.instance;
    },
  },
  methods: {
    ...mapActions({
      getFilteredScoutings: "scouting/getFilteredScoutings",
      deleteObject: "scouting/deleteScouting",
    }),
    // flatten devextreme filter row expression (possibly nested with "and"/"or") into flat { field: value }
    parseGridFilter(filter) {
      const result = {};
      const toYmd = (value) => {
        if (!value) return null;
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return format(date, "yyyy-MM-dd");
      };

      const flatten = (part) => {
        if (!Array.isArray(part)) return;
        if (typeof part[0] === "string" && typeof part[1] === "string" && part.length === 3) {
          const [field, operation, value] = part;

          if (field === "reported_date") {
            if (operation === "between" && Array.isArray(value)) {
              const from = toYmd(value[0]);
              const to = toYmd(value[1]);
              if (from) result.creation_date_from = from;
              if (to) result.creation_date_to = to;
              return;
            }

            if (operation === ">=" || operation === ">") {
              const from = toYmd(value);
              if (from) result.creation_date_from = from;
              return;
            }

            if (operation === "<=" || operation === "<") {
              const to = toYmd(value);
              if (to) result.creation_date_to = to;
              return;
            }

            if (operation === "=") {
              const exact = toYmd(value);
              if (exact) {
                result.creation_date_from = exact;
                result.creation_date_to = exact;
              }
              return;
            }
          }

          result[field] = value;
          return;
        }
        part.forEach((item) => flatten(item));
      };
      flatten(filter);
      return result;
    },
    formatBbch(scouting) {
      let bbch = scouting.bbch_value_from?.name || "-";
      if (scouting.bbch_value_to) {
        bbch += " - " + scouting.bbch_value_to.name;
      }
      return bbch;
    },
    getBbchExportValue(row) {
      return this.formatBbch(row);
    },
    getDiseasesExportValue(row) {
      if (!Array.isArray(row?.diseases) || !row.diseases.length) return "-";
      const names = row.diseases.map((disease) => disease?.name).filter(Boolean);

      return names.length ? names.join(", ") : "-";
    },
    infectionLevelLabel(level) {
      return this.infectionLevelList.find((item) => item.id === level)?.label || "-";
    },
    getInfectionLevelValue(row) {
      return Number(row?.diseases?.[0]?.infection_level) || null;
    },
    allowDeleting() {
      return this.canDelete;
    },
    callAfterResetFilters() {
      this.filters = { creation_date_from: null, creation_date_to: null };
    },
    openColumnChooser() {
      this.dataGridInstance?.showColumnChooser();
    },
    async exportGrid() {
      const instance = this.dataGridInstance;
      if (!instance) return;

      const dataSource = instance.getDataSource();
      const originalPageSize = dataSource?.pageSize?.();
      const originalPageIndex = dataSource?.pageIndex?.();
      const currentSort = typeof dataSource?.sort === "function" ? dataSource.sort() : null;
      const currentFilter = typeof dataSource?.filter === "function" ? dataSource.filter() : null;

      const fileName = "Scoutings_" + format(new Date(), "yyyy_MM_dd_HH_mm_ss");
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Scoutings");

      try {
        if (dataSource?.totalCount && dataSource?.pageSize && dataSource?.pageIndex && dataSource?.load) {
          const totalCount = await dataSource.totalCount();

          if (Number.isFinite(totalCount) && totalCount > 0 && totalCount !== originalPageSize) {
            this.exportSortOverride = Array.isArray(currentSort) ? currentSort : null;
            this.exportFilterOverride = Array.isArray(currentFilter) ? currentFilter : null;
            this.exportLimitOverride = totalCount;
            dataSource.pageIndex(0);
            dataSource.pageSize(totalCount);
            await dataSource.load();
          }
        }

        await exportDataGrid({ component: instance, worksheet, autoFilterEnabled: true });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName + ".xlsx");
      } finally {
        this.exportSortOverride = null;
        this.exportFilterOverride = null;
        this.exportLimitOverride = null;
        if (dataSource?.pageSize && dataSource?.pageIndex && dataSource?.load) {
          if (Number.isFinite(originalPageSize) && originalPageSize > 0) {
            dataSource.pageSize(originalPageSize);
          }
          if (Number.isFinite(originalPageIndex) && originalPageIndex >= 0) {
            dataSource.pageIndex(originalPageIndex);
          }
          await dataSource.load();
        }
      }
    },
  },
};
</script>

<style scoped></style>
